/**
 * Compact relative-time formatter for the HD-96 live-feed rows.
 *
 * Produces strings like "12 s ago", "5 min ago", "3 h ago", "2 d ago".
 * Beyond 7 days, falls back to a short absolute date ("May 4"). All
 * arithmetic is local-clock; we accept a small skew vs. server `now`.
 *
 * Implementation note: we avoid `Intl.RelativeTimeFormat`'s default
 * verbose strings ("3 hours ago") because the live-feed row has very
 * tight horizontal space — compact unit suffixes match the existing
 * mocked design.
 */

const SECOND_MS = 1_000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Returns a compact "<n> <unit> ago" string. Future timestamps are
 * clamped to "just now" — the data model only stores past events.
 *
 * @param timestampIso ISO-8601 timestamp from the server
 * @param now          Optional clock for tests; defaults to `Date.now()`
 */
export function formatRelativeTime(timestampIso: string, now?: number): string {
  const ts = Date.parse(timestampIso);
  if (Number.isNaN(ts)) return '';
  const reference = now ?? Date.now();
  const delta = Math.max(0, reference - ts);

  if (delta < 10 * SECOND_MS) return 'just now';
  if (delta < MINUTE_MS) return `${Math.floor(delta / SECOND_MS)} s ago`;
  if (delta < HOUR_MS) return `${Math.floor(delta / MINUTE_MS)} min ago`;
  if (delta < DAY_MS) return `${Math.floor(delta / HOUR_MS)} h ago`;
  if (delta < WEEK_MS) return `${Math.floor(delta / DAY_MS)} d ago`;

  // Beyond a week — short absolute date in the viewer's locale.
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
